<template>
  <div class="bg-neutral p-2.5 rounded-md" v-if="node">
    <div class="flex justify-between items-center gap-3">
      <div class="flex-1">
        <button
          class="btn btn-primary btn-sm rounded border-none h-max p-2"
          @click="clickShowInputModal('name')"
        >
          <span class="inline-block font-normal"
            >{{ node.name || $t("Buttons.editName") }}
            <i class="iconify mingcute--edit-3-line inline-block"></i>
          </span>
        </button>
      </div>
      <div class="flex gap-1">
        <button
          :class="[
            'btn btn-primary btn-sm rounded border-none h-max font-normal',
            bgm ? 'text-[#E84A4A]' : '',
          ]"
          @click="clickSelectBgm"
        >
          {{ bgm ? bgm : t("Buttons.chooseBackgroundMusic") }}
        </button>
        <button
          class="btn btn-primary btn-sm rounded border-none h-max font-normal"
          @click="clickAutoGenerate"
        >
          {{ t(`story.auto`) }}
        </button>
      </div>
    </div>
    <p
      class="bg-primary p-3 my-2.5"
      @click="clickShowInputModal('description')"
    >
      {{ node.description || $t("Buttons.editPlotPointOverviewDescription") }}
      <i class="iconify mingcute--edit-3-line"></i>
    </p>

    <!-- 对话 -->
    <div v-if="node.dialogzzCount" class="my-2.5">
      <div class="flex justify-between items-center">
        <div class="flex items-center text-sm">
          <i class="iconfont icon-conversation mr-2"></i>
          <span>{{ t("game.node.conversation") }}</span>
        </div>
        <button
          class="btn btn-primary btn-sm rounded border-none"
          @click="clickDialog"
        >
          {{ t("game.node.conversationEdit") }}
        </button>
      </div>
      <div class="text-xs mt-1.5">
        {{ t("game.node.conversationCount") }}{{ node.dialogzzCount }}
      </div>
    </div>

    <!-- 连接 -->
    <div v-if="showConnection" class="my-2.5">
      <div class="flex justify-between items-center">
        <div class="flex items-center text-sm">
          <i class="iconfont icon-connection mr-2"></i>
          <span>{{ $t("game.node.connect") }}</span>
        </div>
        <button
          class="btn btn-primary btn-sm rounded border-none h-max"
          @click="clickConnection"
        >
          {{ t("game.node.connectEdit") }}
        </button>
      </div>
      <div class="text-xs mt-1.5">
        <div v-if="node.score" class="my-2">
          <span> {{ t("game.node.trigger") }}：</span>
          <span class="inline-block bg-primary h-5 leading-5 px-1">
            {{ store.getScoreTextByEx(node.score!) }}
          </span>
        </div>
        <div v-if="node.parentNodeIdzz!.length > 0" class="my-2">
          <span>{{ t("game.node.enter") }}：</span>
          <template v-for="item in node.parentNodeIdzz">
            <span
              v-if="store.nodeMap[item]"
              class="inline-block bg-primary h-5 leading-5 px-1 mr-1"
            >
              {{ store.nodeMap[item].name }}
            </span>
          </template>
        </div>
        <div v-if="node.childNodeIdzz!.length > 0" class="my-2">
          <span>{{ t("game.node.to") }}：</span>
          <template v-for="item in node.childNodeIdzz">
            <span
              v-if="store.nodeMap[item]"
              class="inline-block bg-primary h-5 leading-5 px-1 mr-1"
            >
              {{ store.nodeMap[item].name }}
            </span>
          </template>
        </div>
      </div>
    </div>

    <!-- 添加 -->
    <div class="flex my-3.5">
      <button
        v-if="!node.dialogzzCount"
        class="btn btn-primary btn-sm bg-primary rounded border-none mr-1"
        @click="clickDialog"
      >
        <i class="iconfont icon-conversation text-sm"></i>
        {{ $t("Buttons.editPlotDialogue") }}
      </button>
      <button
        v-if="!showConnection"
        class="btn btn-primary btn-sm rounded border-none mr-2"
        @click="clickConnection"
      >
        <i class="iconfont icon-connection text-sm"></i>
        {{ $t("Buttons.connectPlotPoint") }}
      </button>
    </div>

    <!-- 管理 -->
    <div v-if="node.type !== 1" class="flex justify-end gap-1">
      <button class="btn btn-xs rounded" @click="clickClone">
        <i class="iconfont icon-copy text-xs"></i>
        {{ t(`trait.feature.clone`) }}
      </button>
      <button v-if="!isTop" class="btn btn-xs rounded" @click="clickSort(true)">
        <i class="iconfont icon-up text-xs"></i>
      </button>
      <button
        v-if="!isBottom"
        class="btn btn-xs rounded"
        @click="clickSort(false)"
      >
        <i class="iconfont icon-down text-xs"></i>
      </button>
      <button class="btn btn-xs rounded" @click="clickDelete">
        <i class="iconfont icon-delete text-xs"></i>
      </button>
    </div>
  </div>

  <GalGamePreviewPopup
    :id="store.galGameId"
    ref="previewPopupRef"
    :uuid="store.galGameUUID"
  />
</template>

<script lang="ts" setup>
import { ApiPostGalGameStoryNodeComplete } from "@/apis/gal-game";
import { useGalGameEditor } from "@/stores/galGameEditor";
import { useTone } from "@/stores/tone";
import { storeToRefs } from "pinia";
import { showConfirmDialog } from "vant";
import { PropType, computed } from "vue";
import { useI18n } from "vue-i18n";
import { GalGamePreviewPopupInstance } from "@/components/mobile/gal-game/editor/GalGamePreviewPopup.vue";
import { ref } from "vue";
import i18n from "@/locales";
import { useRouter } from "vue-router";

const previewPopupRef = ref<null | GalGamePreviewPopupInstance>(null);
const props = defineProps({
  node: {
    type: Object as PropType<GalGameNode>,
    required: true,
  },
});

const store = useGalGameEditor();
const toneStore = useTone();
const { bgmList } = storeToRefs(toneStore);
const { t } = useI18n();
const router = useRouter();

const clickShowInputModal = (type: "name" | "description") => {
  store.showInputModal = true;
  store.modalInputContent = props.node[type] || "";
  store.modalInputType = type;
  store.activeNode = props.node;
};

const bgm = computed(() => {
  if (!props.node.bgmMediaId && !store.form.bgmMediaId) return "";
  return (
    bgmList.value.find(
      (v) => v.mediaId === (props.node.bgmMediaId || store.form.bgmMediaId)
    )?.name ?? ""
  );
});

const clickDelete = () => {
  showConfirmDialog({
    title: i18n.global.t("ConfirmModal.deleteNode"),
    message: i18n.global.t("ConfirmModal.deleteNodeText"),
    confirmButtonText: i18n.global.t("Buttons.confirm"),
    cancelButtonText: i18n.global.t("Buttons.cancel"),
  })
    .then(() => {
      store.deleteNode(props.node);
    })
    .catch(() => {});
};

const clickDialog = () => {
  store.isInitDialog = false;
  store.scrollTopDialogCache = 0;
  router.push({
    name: "galGameEditorDialog",
    params: { uuid: store.galGameUUID },
    query: { nodeId: props.node.id },
  });
};

const clickConnection = () => {
  router.push({
    name: "galGameEditorConnect",
    params: { uuid: store.galGameUUID },
    query: {
      nodeId: props.node.id,
    },
  });
};

const clickSort = (isUp: boolean) => {
  const { id, type } = props.node;
  let list: number[] = [];
  if (type === 2) {
    // plot
    list = store.plotList;
  } else if (type === 3) {
    // ending
    list = store.endingList;
  }
  const idx = list.findIndex((item) => item === id);
  if (isUp) {
    const prev = list[idx - 1];
    list[idx - 1] = id!;
    list[idx] = prev;
  } else {
    const next = list[idx + 1];
    list[idx + 1] = id!;
    list[idx] = next;
  }
  store.sortNode();
};

const showConnection = computed(() => {
  const { parentNodeIdzz, childNodeIdzz, score } = props.node;
  if (
    score !== null ||
    (parentNodeIdzz && parentNodeIdzz.length) ||
    (childNodeIdzz && childNodeIdzz.length)
  ) {
    return true;
  }
  return false;
});

const isTop = computed(() => {
  const { plotList, endingList } = store;
  const { type } = props.node;
  if (type === 2) return plotList[0] === props.node.id;
  if (type === 3) return endingList[0] === props.node.id;
});

const isBottom = computed(() => {
  const { plotList, endingList } = store;
  const { type } = props.node;
  if (type === 2) return plotList[plotList.length - 1] === props.node.id;
  if (type === 3) return endingList[endingList.length - 1] === props.node.id;
});

const clickAutoGenerate = () => {
  const { name, description, galGameId } = props.node;
  store.activeNode = props.node;
  store.generateTarget = "node";
  store.isGenerating = true;
  store.generateContent = "";
  store.showGenerateLoadingModal = true;
  try {
    ApiPostGalGameStoryNodeComplete(
      {
        galGameId: galGameId!,
        title: name!,
        description:
          (description ?? "") +
          `"""${i18n.global.t("Texts.completeContent")}"""`,
      },
      callback as any
    );

    function callback(text: any) {
      if (typeof text === "object") {
        store.generateContent = text.data.text;
        store.isGenerating = false;
      } else store.generateContent += text;
    }
  } catch (e) {
    console.log(e);
  }
};

const clickSelectBgm = () => {
  store.activeNode = props.node;
  store.bgmType = 1;
  store.currentBgmId = props.node.bgmMediaId
    ? toneStore.getBgmByMediaId(props.node.bgmMediaId)?.id ?? 0
    : 0;
  store.showBgmModal = true;
};

const clickClone = async () => {
  store.showCloneNodeModal = true;
  store.activeCloneNodeId = props.node.id!;
};
</script>

<style scoped>
/* Your CSS code goes here */
</style>
